import { useEffect, useState } from 'react';
import api from '../services/api';
import type { MetodoPagamento } from '../utils/pagamenti';

export interface DestinationDiv {
  dd_coddest?: number | null;
  dd_nomdest?: string | null;
  inddest?: string | null;
  citta?: string | null;
  prov?: string | null;
  cap?: string | null;
  telef?: string | null;
  email?: string | null;
}

export interface AgenteContatto {
  an_conto?: number | null;
  ragioneSociale?: string | null;
  nome?: string | null;
  cognome?: string | null;
  email?: string | null;
  telefono?: string | null;
  cellulare?: string | null;
}

export interface MyAnagrafica {
  /** `null` per un Contatto: il Cliente su NTS nasce al primo ordine. */
  an_conto: number | null;
  /** Account senza Cliente: i dati sono quelli della registrazione, salvati
   *  solo sul portale; niente destinazioni né condizioni NTS. */
  contatto?: boolean;
  ragioneSociale: string;
  ragioneSocialeAlt?: string | null;
  partitaIva?: string | null;
  codiceFiscale?: string | null;
  sdiCode?: string | null;
  pec?: string | null;
  // Indirizzo sede
  indirizzo?: string | null;
  cap?: string | null;
  citta?: string | null;
  provincia?: string | null;
  paese?: string | null;
  // Contatti
  email?: string | null;
  emailAlt?: string | null;
  telefono?: string | null;
  cellulare?: string | null;
  fax?: string | null;
  // Persona referente
  nome?: string | null;
  cognome?: string | null;
  // Condizioni commerciali (riquadro della dashboard)
  codicePagamento?: number | null;
  codicePagamentoLabel?: string | null;
  codpagLabel?: string | null;  // alias retro-compat
  /** Metodi del checkout ammessi dalla condizione di pagamento
   *  (`orders/pagamenti.py`): il differito solo a chi lo ha in anagrafica. */
  pagamentoDifferito?: boolean | null;
  pagamentiAmmessi?: MetodoPagamento[] | null;
  pagamentoPredefinito?: MetodoPagamento | null;
  /** Codice del porto (`an_porto`, stringa come `tb_codport`) e descrizione NTS. */
  porto?: string | number | null;
  portoLabel?: string | null;
  /** Vettore 1 (`an_vett`): nome da `lookup_vettori`, "Vettore {codice}" se ignoto. */
  vettoreCodice?: number | null;
  vettoreLabel?: string | null;
  /** Flag "Vett. Pref." (`an_hhegvettpref`). */
  vettorePreferenziale?: boolean | null;
  /** `an_acuradi`: "M", "D", "V" o null. */
  trasportoACuraDel?: string | null;
  listino?: number | null;
  listinoLabel?: string | null;
  trasportoMinimo?: number | null;
  // Agente
  agenteCodice?: number | null;
  agenteInternoCodice?: number | null;
  agenteInternoNome?: string | null;
  agenteInternoEmail?: string | null;
  agenteInternoTelefono?: string | null;
  agenteEsternoNome?: string | null;
  agenteEsternoEmail?: string | null;
  agenteEsternoTelefono?: string | null;
  agente?: AgenteContatto | null;
  // Destinazioni
  destinazioni: DestinationDiv[];
  destinazionePreferita?: number | null;
  // Status
  status?: string | null;
}

/**
 * Recupera l'anagrafica completa del cliente loggato.
 * Caching client-side basato su window per evitare round-trip ripetuti
 * tra pagine dello stesso layout.
 */
const CACHE: { data: MyAnagrafica | null; ts: number } = { data: null, ts: 0 };
const TTL_MS = 5 * 60 * 1000;

/** Scarta la copia in cache: la prossima pagina che usa l'hook la rilegge.
 *  Da chiamare quando l'anagrafica cambia fuori dalla pagina che la mostra,
 *  per esempio quando il primo ordine di un Contatto crea il Cliente. */
export function invalidaAnagrafica() {
  CACHE.data = null;
  CACHE.ts = 0;
}

export function useMyAnagrafica(enabled: boolean = true): { data: MyAnagrafica | null; loading: boolean; error: string | null; reload: () => void } {
  const [data, setData] = useState<MyAnagrafica | null>(CACHE.data);
  const [loading, setLoading] = useState(enabled && !CACHE.data);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!enabled) { setLoading(false); return; }
    const fresh = CACHE.data && Date.now() - CACHE.ts < TTL_MS;
    if (fresh && refreshTick === 0) {
      setData(CACHE.data); setLoading(false); return;
    }
    setLoading(true);
    api.get<any>('/api/clienti/me/anagrafica')
      .then(r => {
        // Normalizza: l'API può restituire `codiceSDI` (nome Pydantic) → mappa anche su `sdiCode`
        const raw = r.data;
        const mapped: MyAnagrafica = {
          ...raw,
          sdiCode: raw.sdiCode ?? raw.codiceSDI ?? null,
        };
        CACHE.data = mapped; CACHE.ts = Date.now();
        setData(mapped); setError(null);
      })
      .catch(e => setError(e?.response?.data?.detail ?? e?.message ?? 'Errore'))
      .finally(() => setLoading(false));
  }, [refreshTick, enabled]);

  return { data, loading, error, reload: () => setRefreshTick(t => t + 1) };
}
